import { useState } from 'react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

interface WelcomeScreenProps {
  tableCode?: string;          // undefined = public browse link (/r/{slug}), no table
  outletName: string;
  gstInclusive?: boolean;
  onStartOrdering: (name: string, phone: string, voice: boolean) => void;
}

export default function WelcomeScreen({ tableCode, outletName, gstInclusive = false, onStartOrdering }: WelcomeScreenProps) {
  const browseMode = !tableCode;
  const [tableConfirmed, setTableConfirmed] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);

  const phoneDigits = phone.replace(/\D/g, '').slice(-10);
  const nameOk = name.trim().length > 0;
  const phoneOk = phoneDigits.length === 10;
  const tableOk = browseMode || tableConfirmed;
  const canStart = tableOk && nameOk && phoneOk;

  // Table first, then details. Clicking a CTA before confirming the table just
  // confirms it; after that we require a name + a valid 10-digit phone.
  const start = (voice: boolean) => {
    if (!tableOk) { setTableConfirmed(true); return; }
    if (!canStart) { setTouched(true); return; }
    onStartOrdering(name.trim(), phoneDigits, voice);
  };

  return (
    <div className="relative min-h-screen flex flex-col bg-background-50 overflow-hidden">
      {/* Hero image background */}
      <div className="absolute inset-0 h-[55%]">
        <img
          src="/images/selforder-welcome.jpg"
          alt="Restaurant ambiance"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-background-50" />
      </div>

      {/* Language chooser — offered before ordering, so a diner reads the flow
          in their own language from the first screen. */}
      <div className="absolute right-4 top-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col flex-1">
        {/* Top spacing */}
        <div className="flex-1" />

        {/* Welcome card */}
        <div className="px-6 pb-8">
          <div className="bg-background-50 rounded-2xl p-8 text-center w-full max-w-md mx-auto animate-fade-in-up">
            {/* Brand */}
            <div className="w-16 h-16 mx-auto mb-4 bg-primary-500 rounded-2xl flex items-center justify-center">
              <i className="ri-restaurant-2-fill text-3xl text-background-50"></i>
            </div>
            <h1 className="text-2xl font-semibold text-foreground-950 font-heading mb-1">
              {outletName}
            </h1>
            <p className="text-foreground-500 text-sm mb-6">Scan, order, enjoy — no waiting for a server</p>

            {/* Table confirmation (hidden on public browse link) */}
            {!browseMode && (
            <div className="bg-background-100 rounded-xl p-4 mb-6">
              <p className="text-xs text-foreground-500 uppercase tracking-wide font-label mb-2">Your Table</p>
              {tableConfirmed ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-3xl font-bold text-foreground-950 font-heading">Table {tableCode}</span>
                  <button
                    onClick={() => setTableConfirmed(false)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 hover:bg-background-300 transition-colors cursor-pointer"
                    title="Change table"
                  >
                    <i className="ri-pencil-line text-xs text-foreground-600"></i>
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="text"
                    defaultValue={tableCode}
                    className="w-20 text-center text-2xl font-bold text-foreground-950 bg-background-50 border-2 border-primary-500 rounded-lg py-2 px-1 focus:outline-none focus:ring-2 focus:ring-primary-400 font-heading"
                    placeholder="T#"
                    maxLength={5}
                    onKeyDown={(e) => { if (e.key === 'Enter') setTableConfirmed(true); }}
                  />
                  <button
                    onClick={() => setTableConfirmed(true)}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
            )}

            {/* Name */}
            <div className="mb-3 text-left">
              <label className="text-xs text-foreground-500 uppercase tracking-wide font-label">Your name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Raj"
                className={`mt-1 w-full text-center text-base font-medium text-foreground-950 bg-background-100 border rounded-xl py-3 px-3 focus:outline-none focus:ring-2 focus:ring-primary-400 ${
                  touched && !nameOk ? 'border-red-400' : 'border-background-200'
                }`}
                maxLength={40}
              />
            </div>

            {/* Phone */}
            <div className="mb-4 text-left">
              <label className="text-xs text-foreground-500 uppercase tracking-wide font-label">Mobile number</label>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="10-digit mobile"
                className={`mt-1 w-full text-center text-base font-medium text-foreground-950 bg-background-100 border rounded-xl py-3 px-3 focus:outline-none focus:ring-2 focus:ring-primary-400 ${
                  touched && !phoneOk ? 'border-red-400' : 'border-background-200'
                }`}
                maxLength={15}
              />
              <p className="text-[11px] text-foreground-400 mt-1 text-center">
                We use this to link your order and update you when it&apos;s ready.
              </p>
            </div>

            {touched && !canStart && (
              <p className="text-xs text-red-600 mb-3">
                {!nameOk ? 'Please enter your name.' : 'Enter a valid 10-digit mobile number.'}
              </p>
            )}

            {/* CTAs */}
            <button
              onClick={() => start(true)}
              className="w-full py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-base rounded-xl transition-all duration-200 active:scale-[0.98] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-mic-fill"></i> Order by Talking (AI)
            </button>
            <button
              onClick={() => start(false)}
              className="w-full mt-2.5 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm rounded-xl transition-all duration-200 active:scale-[0.98] cursor-pointer whitespace-nowrap"
            >
              Browse Menu & Order
              <i className="ri-arrow-right-line ml-2"></i>
            </button>

            <p className="text-foreground-400 text-xs mt-4">
              By ordering, you agree to our terms. {gstInclusive ? 'All prices include GST.' : 'GST is applied at checkout.'}
            </p>
          </div>
        </div>

        {/* Bottom safe area */}
        <div className="h-6" />
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}